import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { string } from 'prop-types';

import { Mutation } from 'react-apollo';
import GetUserRecipeBox from 'components/GetUserRecipeBox';
import CREATE_USER_RECIPE_TAGS_MUTATION from 'graphql/createUserRecipeTags';
import TOGGLE_RECIPE_TAG from 'graphql/updateRecipeInBox';

import { toggleRecipeInCache, createRecipeInCache } from './helpers';

import styles from './styles';

function RecipeBoxTagForm({ userRecipeId, recipeId, tagTitle }) {
  // state
  const [isAddNewTagLoading, setIsAddNewTagLoading] = useState(false);
  const [tagLoading, setTagLoading] = useState(undefined);
  const [stateError, setError] = useState(undefined);
  const [addTagValue, setAddTagValue] = useState('');
  const [addedTags, setAddTag] = useState([]);
  const tagInput = useRef(null);

  // actions
  const toggleTag = (currentRecipeTags, toggleRecipeTag, tag) => {
    // if tag id is included in either state or recipe tags filter out the tag from the array
    // else add the tag into the recipes tag array

    // currentTags = addedTags state if we have component state. Otherwise, use tags that have come from recipe BE
    const currentTags = addedTags.length !== 0 ? addedTags : currentRecipeTags;

    // get current list of tag ids to be updated
    const tagsIds =
      // addedTags.includes(tag.recipeTagId) ||
      currentTags.includes(tag.recipeTagId)
        ? currentTags.filter(t => t !== tag.recipeTagId)
        : [...currentTags, tag.recipeTagId];
    toggleRecipeTag({
      variables: {
        input: {
          userRecipeId,
          tagsIds
        }
      },
      update: toggleRecipeInCache(userRecipeId, tag, recipeId)
    })
      .then((result, error) => {
        if (error) {
          setError(error);
          setTagLoading(undefined);
        }
        if (result.data.updateRecipeInBox !== undefined) {
          setAddTag(tagsIds);
          setTagLoading(undefined);
        }
      })
      .catch(error => {
        setError(error);
        setTagLoading(undefined);
      });
    setTagLoading(tag.recipeTagId);
  };

  const onAddTag = (createRecipeTags, currentRecipeTags, toggleRecipeTag) => {
    const tags = addTagValue
      .split(',')
      .map(tag => tag.trim().toLowerCase()) // remove space from tags all tags and lowercase
      .filter(el => el !== '');

    createRecipeTags({
      variables: {
        input: {
          names: tags
        }
      },
      update: createRecipeInCache(userRecipeId)
    })
      .then((result, error) => {
        if (error) {
          setError(error);
          setIsAddNewTagLoading(false);
        }
        if (result.data.createRecipeTags !== undefined) {
          // now that the tag has been added we want to make sure it is applied to the recipe
          const { recipeTags } = result.data.createRecipeTags;
          // If a dupe has been found don't try to toggle the button on
          if (recipeTags.length > 0) {
            recipeTags.forEach(rt =>
              toggleTag(currentRecipeTags, toggleRecipeTag, rt)
            );
          }

          // update state to clear input and clear loading
          setAddTagValue('');
          setIsAddNewTagLoading(false);
        }
      })
      .catch(error => {
        setError(error);
        setIsAddNewTagLoading(false);
      });
    setIsAddNewTagLoading(true);
  };

  const inputContainsMultipleTags =
    addTagValue && addTagValue.indexOf(',') !== -1;

  return (
    <Mutation mutation={TOGGLE_RECIPE_TAG}>
      {toggleRecipeTag => (
        <Mutation mutation={CREATE_USER_RECIPE_TAGS_MUTATION}>
          {createRecipeTags => (
            <GetUserRecipeBox>
              {({ data: userData }) => {
                // get current recipe and find tags and pull out just the current recipe ids
                const currentRecipe =
                  userData.recipeBox &&
                  userData.recipeBox.recipes.find(
                    recipe => recipe.userRecipeId === userRecipeId
                  );
                const currentRecipeTags =
                  (currentRecipe &&
                    currentRecipe.tags.map(tag => tag.recipeTagId)) ||
                  [];
                return (
                  <div className="RecipeBoxTagForm">
                    <style jsx>{styles}</style>
                    <div className="RecipeBoxTagForm__myTags">
                      <strong>{tagTitle}</strong>
                      <div className="RecipeBoxTagForm__usedTagForm">
                        {userData.recipeBox &&
                          userData.recipeBox.tags.map(tag => (
                            <button
                              key={tag.name}
                              disabled={isAddNewTagLoading}
                              onClick={() => {
                                toggleTag(
                                  currentRecipeTags,
                                  toggleRecipeTag,
                                  tag
                                );
                              }}
                              className={classnames(
                                'button',
                                addedTags.includes(tag.recipeTagId) ||
                                  currentRecipeTags.includes(tag.recipeTagId)
                                  ? 'button--hasBeenAdded'
                                  : ''
                              )}
                            >
                              {tagLoading === tag.recipeTagId
                                ? '...'
                                : tag.name}
                            </button>
                          ))}
                      </div>
                    </div>
                    <div className="RecipeBoxTagForm__addTagForm">
                      <div className="RecipeBoxTagForm__addTagInput">
                        <input
                          placeholder="e.g. Weeknight"
                          ref={tagInput}
                          type="text"
                          value={addTagValue}
                          onChange={() =>
                            setAddTagValue(tagInput.current.value)
                          }
                        />
                        <button
                          disabled={
                            addTagValue === '' ||
                            isAddNewTagLoading ||
                            inputContainsMultipleTags
                          }
                          onClick={() =>
                            isAddNewTagLoading === false
                              ? onAddTag(
                                  createRecipeTags,
                                  currentRecipeTags,
                                  toggleRecipeTag
                                )
                              : null
                          }
                          className="button"
                        >
                          {isAddNewTagLoading ? 'Saving...' : 'Add'}
                        </button>
                        {stateError && (
                          <div className="error">{stateError}</div>
                        )}
                      </div>
                      <p>
                        {inputContainsMultipleTags
                          ? 'Please enter one tag at a time.'
                          : 'Add as many tags as you like, separated by commas'}
                      </p>
                    </div>
                  </div>
                );
              }}
            </GetUserRecipeBox>
          )}
        </Mutation>
      )}
    </Mutation>
  );
}

RecipeBoxTagForm.propTypes = {
  recipeId: string.isRequired,
  userRecipeId: string,
  tagTitle: string
};

RecipeBoxTagForm.defaultProps = {
  userRecipeId: null,
  tagTitle: 'My Tags'
};

export default RecipeBoxTagForm;
